import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, cfmt, pfmt } from '../lib/api';
import { TID } from '../constants/testIds';
import Sparkline from '../components/common/Sparkline';

export default function Invest() {
    const { data } = useQuery({
        queryKey: ['invest-top'],
        queryFn: async () => (await api.get('/invest/top', { params: { limit: 20 } })).data,
    });

    const items = data?.top || [];
    const market = data?.market || {};

    return (
        <div className="bg-[#050505] text-white">
            <section className="border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 pb-10">
                    <div className="overline text-neutral-400">Investment terminal</div>
                    <h1 className="font-editorial text-[44px] lg:text-[64px] tracking-editorial mt-4 leading-[1.04]">
                        The neighbourhoods most likely to appreciate.
                    </h1>
                    <p className="mt-6 text-[15px] text-neutral-400 max-w-2xl">
                        Ranked by our five-year forecast. The model weighs transit velocity, zoning changes, amenity density,
                        and the trend momentum from the last five transaction years.
                    </p>
                </div>
            </section>

            <section className="border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 border-l border-t border-white/10">
                    {[
                        { l: 'Avg 5Y forecast', v: `+${pfmt(market.avg_forecast_5yr || 0)}`, tone: 'up' },
                        { l: 'Avg 5Y actual', v: `+${pfmt(market.avg_5yr_change || 0)}` },
                        { l: 'Avg 1Y', v: `${(market.avg_1yr_change || 0) >= 0 ? '+' : ''}${pfmt(market.avg_1yr_change || 0)}` },
                        { l: 'Tracked areas', v: market.tracked_neighbourhoods || 0 },
                    ].map((s) => (
                        <div key={s.l} className="border-r border-b border-white/10 p-6">
                            <div className="overline text-neutral-500">{s.l}</div>
                            <div className={`font-mono-data text-[26px] tabular mt-2 ${s.tone === 'up' ? 'text-emerald-400' : ''}`}>{s.v}</div>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10" data-testid={TID.invest.list}>
                    <div className="grid grid-cols-12 py-3 px-4 text-[11px] text-neutral-500 uppercase tracking-[0.18em] border-b border-white/10">
                        <div className="col-span-1">#</div>
                        <div className="col-span-4">Neighbourhood</div>
                        <div className="col-span-2 text-right">Median</div>
                        <div className="col-span-1 text-right">1Y</div>
                        <div className="col-span-1 text-right">5Y</div>
                        <div className="col-span-1 text-right">Fcast</div>
                        <div className="col-span-2 text-right">Trend</div>
                    </div>
                    {items.map((n, i) => (
                        <Link key={n.slug}
                            to={`/neighbourhoods/${n.slug}`}
                            data-testid={TID.invest.card}
                            className="grid grid-cols-12 py-4 px-4 border-b border-white/5 hover:bg-white/5 items-center text-[14px]">
                            <div className="col-span-1 font-mono-data tabular text-neutral-500">{String(i + 1).padStart(2, '0')}</div>
                            <div className="col-span-4">
                                <div className="font-editorial text-[19px] tracking-editorial">{n.name}</div>
                                <div className="text-[11px] text-neutral-500 uppercase tracking-wider mt-0.5">{n.city} · {n.province}</div>
                            </div>
                            <div className="col-span-2 text-right font-mono-data tabular">{cfmt(n.median_price)}</div>
                            <div className={`col-span-1 text-right font-mono-data tabular ${n.price_1yr_change_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                {n.price_1yr_change_pct >= 0 ? '+' : ''}{pfmt(n.price_1yr_change_pct)}
                            </div>
                            <div className="col-span-1 text-right font-mono-data tabular text-emerald-400">+{pfmt(n.price_5yr_change_pct)}</div>
                            <div className="col-span-1 text-right font-mono-data tabular text-emerald-300">+{pfmt(n.forecast_5yr_pct)}</div>
                            <div className="col-span-2 flex justify-end">
                                <Sparkline data={n.price_history} width={110} height={24} />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
